import React from 'react'
import { Redirect } from 'react-router'

import { RouteProps, routes } from '@sketch/constants'
import { PrivateWorkspaceRouteExtraProps } from 'modules/workspace/containers'

// Components
import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import WorkspaceSidebarLayout from 'modules/workspace/components/WorkspaceSidebarLayout'
import NotFoundView from 'views/NotFoundView'

// Local components
import { HiddenProfileBanner } from '../components/HiddenProfileBanner'
import { EditingProfileNavBar } from '../components/EditingProfileNavBar'
import { WorkspaceProfileLayout } from '../containers/WorkspaceProfileLayout'
import { ProfileForm } from '../components/ProfileForm'

// Utils
import { useShowcaseYourDesignModal } from '../modals/ShowcaseYourDesignModal'

// GQL
import { useGetWorkspaces } from 'modules/workspace/operations'
import { useGetWorkspaceProfile } from '../hooks/useGetWorkspaceProfile'
import { useIsProfileEnabled } from '../hooks/useIsProfileEnabled'

interface WorkspaceProfileEditViewProps
  extends RouteProps<'WORKSPACE_PROFILE_EDIT'>,
    PrivateWorkspaceRouteExtraProps {}

const WorkspaceProfileEditView = (props: WorkspaceProfileEditViewProps) => {
  const { workspaceId } = props.match.params
  const { workspace } = props

  useShowcaseYourDesignModal({ workspaceId })

  const { workspaces } = useGetWorkspaces()

  const profileResult = useGetWorkspaceProfile(workspaceId)

  if (profileResult.error) {
    return <NotFoundView />
  }

  const profile = profileResult.profile

  const profileData = {
    identifier: profile?.identifier || '',
    description: profile?.description || '',
    websiteUrl: profile?.websiteUrl || '',
    donateUrl: profile?.donateUrl || '',
  }

  return (
    <WorkspaceSidebarLayout
      title="Profile"
      workspaces={workspaces ?? []}
      currentWorkspace={workspace}
      navbar={
        <EditingProfileNavBar workspaceIdentifier={workspace?.identifier!} />
      }
      subheader={
        profile?.isPublic === false ? (
          <HiddenProfileBanner identifier={profileData.identifier} />
        ) : null
      }
    >
      <WorkspaceProfileLayout
        workspaceId={workspaceId}
        isProfileEditRoute={true}
        profileResult={profileResult}
        profileInfo={
          <ProfileForm data={profileData} loading={profileResult.loading} />
        }
      />
    </WorkspaceSidebarLayout>
  )
}

// At the moment only Admins of the workspace can access
// Profile Edit page. And this is the first component after
// PrivateWorkspaceRoute where we can check this.
// In addition we don't want to trigger useShowcaseYourDesignModal
// for non-admin members. Therefore we have a guard component here.
//
// If the profile is not created yet we also wanna prevent this page
// to be seen.
const WorkspaceProfileEditViewGuard = (
  props: WorkspaceProfileEditViewProps
) => {
  const { workspace } = props
  const workspaceId = workspace.identifier

  const { isProfileEnabled, loading } = useIsProfileEnabled(workspaceId)

  if (workspace.userRole !== 'ADMIN' || (!loading && !isProfileEnabled)) {
    return <Redirect to={routes.WORKSPACE_SHARES.create({ workspaceId })} />
  }

  if (loading) {
    return <DynamicLoadingPage />
  }

  return <WorkspaceProfileEditView {...props} />
}

export default WorkspaceProfileEditViewGuard
